import { faUpRightAndDownLeftFromCenter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/button/Button';
import * as React from 'react';
import { memo } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import {
    buildHierarchy,
    buildTooltip,
    excludePreRootGroupFromHierarchy,
    GenericHierarchyChild,
} from 'utils/group_hierarchy';
import { optional } from 'utils/monad';

const SuffixLink = styled(Button)`
    padding: 0 0.5rem;
    color: ${tokens.text.link};
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;

    &:hover {
        color: ${tokens.text.linkHover};
    }
`;

const ItemButton = styled(Button)`
    padding: 0;
    color: ${tokens.text.link};
    text-decoration: none;

    &:hover {
        color: ${tokens.text.linkHover};
    }
`;

export type HierarchyItemClickHandler = {
    handler: (item: GenericHierarchyChild) => void;
    title: string;
};

export type GroupHierarchyTextProps = Readonly<{
    /**
     * Hierarchy to display.
     */
    hierarchy: GenericHierarchyChild[] | undefined;

    /**
     * Whether to display the additional suffix information from the hierarchy child
     */
    showHierarchySuffix?: boolean;

    /**
     * Optional prefix to add to the beginning of the hierarchy (ex: Location or Corporation).
     */
    prefix?: string;

    /**
     * Optional suffix to add to the end of the hierarchy (ex: an Indicator name).
     */
    suffix?: string;

    /**
     * Class names to apply to the prefix text. If specified, this will override
     * the default class names completely.
     */
    prefixClassName?: string;

    /**
     * Class names to apply to the suffix text. If specified, this will override
     * the default class names completely.
     */
    suffixClassName?: string;

    /**
     * Groups to use as the base of the hierarchy text.
     */
    rootGroupIds?: string[];

    /**
     * When true, the root group will be included in the hierarchy (defaults to `false`).
     */
    includeRootInHierarchy?: boolean;

    /**
     * Class to apply to component.
     */
    className?: string;

    /**
     * If true, the title will not be included. Defaults to `false`.
     */
    hideTitle?: boolean;

    /**
     * Displays hierarchy groups by full name, else abbreviation (defaults to `false`).
     */
    useFullGroupName?: boolean;

    wrapHierarchyString?: boolean;

    /**
     * a component for rendering the last hierarchy item -- this bypasses
     * most-all other modifiers
     */
    LastItem?: React.FC<{ item: GenericHierarchyChild; index: number }>;

    /**
     * Callback for when the suffix is clicked. If supplied, styles the suffix as a clickable link.
     */
    onSuffixClick?: () => void;

    /**
     * An optional function to create a click handler a hierarchy item -- return
     * undefined if there should be no link
     */
    onItemClick?: (item: GenericHierarchyChild) => HierarchyItemClickHandler | undefined;
}>;

const defaultSuffixClassname = 'bold text-nowrap';
const defaultPrefixClassname = 'text-nowrap';

function GroupHierarchyText({
    hierarchy,
    showHierarchySuffix = false,
    prefix,
    suffix,
    LastItem,
    rootGroupIds: parentGroupIds,
    className,
    includeRootInHierarchy = false,
    hideTitle = false,
    prefixClassName = defaultPrefixClassname,
    suffixClassName = defaultSuffixClassname,
    useFullGroupName = false,
    wrapHierarchyString = false,
    onSuffixClick,
    onItemClick,
}: GroupHierarchyTextProps) {
    const filteredHierarchy = hierarchy
        ? excludePreRootGroupFromHierarchy(
              hierarchy,
              parentGroupIds ?? [],
              includeRootInHierarchy
          )
        : [];

    const tooltip = buildTooltip(hierarchy ?? [], prefix, suffix);

    const handleSuffixClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onSuffixClick?.();
    };

    const renderHierarchyWithLinks = () => {
        if (filteredHierarchy.length === 0) {
            return undefined;
        }

        if (!onItemClick && !LastItem && useFullGroupName) {
            return buildHierarchy(
                filteredHierarchy,
                useFullGroupName,
                showHierarchySuffix
            );
        }

        return filteredHierarchy.map((item, index, arr) => {
            if (LastItem && index === arr.length - 1) {
                return <LastItem key={item.HierarchyId} item={item} index={index} />;
            }

            const displayText = useFullGroupName
                ? item.HierarchyName
                : item.HierarchyAbbreviation;
            const suffixText =
                showHierarchySuffix && item.HierarchySuffix
                    ? ` (${item.HierarchySuffix})`
                    : '';
            const fullText = `${displayText}${suffixText}`;

            const clickHandlerInfo = onItemClick?.(item);

            const element = clickHandlerInfo ? (
                <ItemButton
                    key={item.HierarchyId}
                    title={clickHandlerInfo.title}
                    onClick={() => clickHandlerInfo.handler(item)}
                >
                    {fullText}
                </ItemButton>
            ) : (
                <span key={item.HierarchyId}>{fullText}</span>
            );

            return (
                <React.Fragment key={item.HierarchyId}>
                    {element}
                    {index < filteredHierarchy.length - 1 && ' / '}
                </React.Fragment>
            );
        });
    };

    return (
        <span
            className={`${onSuffixClick ? 'flex-row flex-wrap' : ''} ${className ?? ''}`}
            title={hideTitle ? undefined : tooltip}
            aria-label={tooltip}
        >
            {prefix && <span className={prefixClassName}>{prefix}</span>}

            {optional(renderHierarchyWithLinks())
                .map<React.ReactNode>((renderedHierarchy) => (
                    <>
                        {prefix && ' / '}
                        <span className={wrapHierarchyString ? '' : 'text-nowrap'}>
                            {renderedHierarchy}
                        </span>
                        {suffix && ' / '}
                    </>
                ))
                .else(() => prefix && suffix && ' / ')}

            {suffix && (
                <>
                    {onSuffixClick ? (
                        <SuffixLink
                            className={suffixClassName}
                            onClick={handleSuffixClick}
                        >
                            {suffix}
                            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
                        </SuffixLink>
                    ) : (
                        <span className={suffixClassName}>{suffix}</span>
                    )}
                </>
            )}
        </span>
    );
}

const memoGroupHierarchyText = memo(GroupHierarchyText) as typeof GroupHierarchyText;
export { memoGroupHierarchyText as GroupHierarchyText };
